// LogViewer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ApiCall from '../utils/apis';
import '../assets/css/LogViewer.css';

const LogViewer = (props) => {
    const [logData, setLogData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState('today');
    const [availableDates, setAvailableDates] = useState([]);
    const logEndRef = useRef(null);

    const scrollToBottom = () => {
        if (logEndRef.current) {
            logEndRef.current.scrollTop = logEndRef.current.scrollHeight;
        }
    };

    const fetchAvailableDates = useCallback(async () => {
        try {
            const data = await ApiCall({ call: `logDates?botId=${props.botId}`, method: 'GET' });
            if (data && data.dates) {
                setAvailableDates(data.dates);
            }
        } catch (err) {
            console.error('Error al obtener fechas disponibles:', err);
        }
    }, [props.botId]);

    const fetchLog = useCallback(async (date = '') => {
        if (!props.botId) return;
        setLoading(true);
        setError('');

        try {
            let call = `logToday?botId=${props.botId}`;
            if (date && date !== 'today') {
                call += `&date=${date}`;
            }

            const data = await ApiCall({ call, method: 'GET' });

            if (data && data.content !== undefined) {
                setLogData(data);
            } else if (data && data.error) {
                setError(data.error);
                setLogData(null);
            } else {
                setError('Error al cargar el log');
                setLogData(null);
            }
        } catch (err) {
            setError('Error de conexión con el servidor');
            setLogData(null);
        } finally {
            setLoading(false);
        }
    }, [props.botId]);

    const handleDateChange = (event) => {
        const date = event.target.value;
        setSelectedDate(date);
        fetchLog(date);
    };

    const handleRefresh = () => {
        fetchLog(selectedDate);
    };

    const handleLoadToday = () => {
        setSelectedDate('today');
        fetchLog('today');
    };

    // Colorear líneas de log por nivel
    const renderLogLine = (line, index) => {
        let className = 'log-line';
        if (line.includes('- ERROR -')) {
            className += ' log-error';
        } else if (line.includes('- WARNING -')) {
            className += ' log-warning';
        } else if (line.includes('- INFO -')) {
            className += ' log-info-line';
        }
        return (
            <div key={index} className={className}>
                <span className="log-line-number">{index + 1}</span>
                <span className="log-line-content">{line}</span>
            </div>
        );
    };

    // Cargar fechas y log al montar o cambiar de symbol
    useEffect(() => {
        if (props.botId) {
            fetchAvailableDates();
            setSelectedDate('today');
            fetchLog('today');
        }
    }, [props.botId, fetchAvailableDates, fetchLog]);

    // Auto-refresh cada 30 segundos solo si estamos en "today"
    useEffect(() => {
        if (selectedDate !== 'today') return;
        const interval = setInterval(() => {
            fetchLog('today');
        }, 30000);
        return () => clearInterval(interval);
    }, [selectedDate, fetchLog]);

    return (
        <div className="log-viewer">
            {/* Barra de controles */}
            <div className="log-toolbar">
                <div className="log-toolbar-left">
                    <h3 className="log-title">📋 Logs — {props.symbol}</h3>
                </div>
                <div className="log-toolbar-right">
                    <select
                        className="log-date-select"
                        value={selectedDate}
                        onChange={handleDateChange}
                    >
                        <option value="today">📅 Hoy</option>
                        {availableDates
                            .filter(d => d !== 'today')
                            .map(date => (
                                <option key={date} value={date}>{date}</option>
                            ))
                        }
                    </select>

                    <button
                        className="log-btn log-btn-today"
                        onClick={handleLoadToday}
                        title="Cargar log de hoy"
                    >
                        📅 Hoy
                    </button>

                    <button
                        className="log-btn log-btn-refresh"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Recargar"
                    >
                        {loading ? '⏳' : '🔄'} Recargar
                    </button>
                </div>
            </div>

            {/* Metadata */}
            {logData && (
                <div className="log-meta-bar">
                    <span><strong>Fecha:</strong> {logData.date}</span>
                    {logData.line_count !== undefined && (
                        <span><strong>Líneas:</strong> {logData.line_count}</span>
                    )}
                    {logData.filename && (
                        <span><strong>Archivo:</strong> {logData.filename}</span>
                    )}
                    {selectedDate === 'today' && (
                        <span className="log-auto-refresh-badge">🟢 Auto-refresh 30s</span>
                    )}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="log-error-message">
                    ❌ {error}
                </div>
            )}

            {/* Loading */}
            {loading && !logData && (
                <div className="log-loading">
                    ⏳ Cargando logs...
                </div>
            )}

            {/* Contenido del log */}
            <div className="log-content-area" ref={logEndRef}>
                {logData ? (
                    logData.content ? (
                        <div className="log-lines">
                            {logData.content.split('\n').map((line, i) =>
                                line.trim() ? renderLogLine(line, i) : null
                            )}
                        </div>
                    ) : (
                        <div className="log-empty">
                            📝 No hay registros para esta fecha
                        </div>
                    )
                ) : !loading && !error ? (
                    <div className="log-empty">
                        Selecciona un símbolo para ver sus logs
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default LogViewer;